import * as Arr from 'effect/Array';
import * as Data from 'effect/Data';
import * as Either from 'effect/Either';
import * as Fn from 'effect/Function';
import * as Str from 'effect/String';
import * as Utils from '../internal.utils';
import { IbanCountryMeta, PositionLookup, registry } from './iban-registry';

export { IbanCountryMeta, PositionLookup, registry };
export interface UnvalidatedIban {
  readonly _tag: 'UnvalidatedIban';
  /** The ISO 3166-1 alpha-2 country code */
  readonly countryCode: string;
  /** The check digits. */
  readonly checkDigits: string;
  /** The Basic Bank Account Number (BBAN) */
  readonly bban: string;
}
export const UnvalidatedIban = Data.tagged<UnvalidatedIban>('UnvalidatedIban');

/**
 * Represents a valid IBAN
 */
export interface Iban {
  readonly _tag: 'Iban';
  /** The ISO 3166-1 alpha-2 country code */
  readonly countryCode: string;
  /** The check digits. */
  readonly checkDigits: string;
  /** The Basic Bank Account Number (BBAN) */
  readonly bban: string;
}
export const Iban = Data.tagged<Iban>('Iban');

export enum IbanValidity {
  Valid,
  InvalidCheckDigits,
  UnsupportedCountry,
  CountrySpecificInvalidity,
}

// Remove separators
const _asElectronicFormat = (ibanString: string): string =>
  ibanString.replace(/[^A-Za-z0-9]/g, '');

/**
 * @param iban IBAN in print format
 *
 * @returns
 * Instance of IBAN from a parsed print format
 */
export function fromString(iban: string): UnvalidatedIban {
  return Fn.pipe(iban, _asElectronicFormat, partsFromString, UnvalidatedIban);
}

export const printFormat =
  (separator = ' ') =>
  (iban: Iban | UnvalidatedIban | LegacyIban): string =>
    Fn.pipe(iban, electronicFormat, Utils.getGrouped(4), Arr.join(separator));

export const electronicFormat = (
  iban: Iban | UnvalidatedIban | LegacyIban,
): string => `${iban.countryCode}${iban.checkDigits}${iban.bban}`;

export const validate = (
  unvalidated: UnvalidatedIban | LegacyIban,
): Either.Either<Iban, string> =>
  Fn.pipe(
    unvalidated,
    Either.liftPredicate(
      hasValidCheckDigits,
      (n) => `Check digits ${n.checkDigits} are invalid`,
    ),
    Either.map(Fn.flow(Iban)),
  );

function hasValidCheckDigits(
  unvalidated: UnvalidatedIban | LegacyIban,
): boolean {
  const d = getReorderedNumberRepresentation(unvalidated);

  // get the first 2 digits
  let n = Number.parseInt(d.substring(0, 2), 10);
  // and group the rest in groups of 7
  const groups = Utils.getGrouped(d.substring(2), 7);

  for (const group of groups) {
    n = Number.parseInt(`${n}${group}`, 10) % 97;
  }

  return n === 1;
}

function getReorderedNumberRepresentation(
  unvalidated: UnvalidatedIban | LegacyIban,
): string {
  // reorder iban
  const { countryCode, checkDigits, bban } = unvalidated;
  const reordered = `${bban}${countryCode}${checkDigits}`;

  // replace letters with their numeric values
  return reordered.replace(/[A-Z]/g, (char) =>
    getNumericValue(char).toString(),
  );
}

/**
 * @param char
 *
 * @returns
 * The numeric value for the given char, where:
 * A or a = 10
 * Z or z = 35
 */
function getNumericValue(char: string): number {
  // latin unicode letters A-Z are consecutive
  const diff = char.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
  return 10 + diff;
}

function partsFromString(ibanStr: string) {
  const countryCode = Fn.pipe(ibanStr, Str.substring(0, 2), Str.toUpperCase);
  const checkDigits = Fn.pipe(ibanStr, Str.substring(2, 4));
  const bban = Fn.pipe(ibanStr, Str.substring(4), Str.toUpperCase);

  return { countryCode, checkDigits, bban };
}

/**
 * Represents an IBAN.
 *
 * @deprecated use {@link Iban} instead.
 */
export class LegacyIban {
  /**
   * IBAN in electronic format (without separators)
   */
  private _value: string;

  /**
   * @param iban IBAN represented as a string in electronic format
   */
  constructor(iban: string) {
    this._value = iban;
  }

  /**
   * @param iban IBAN in print format
   *
   * @returns
   * Instance of IBAN from a parsed print format
   *
   * @deprecated use {@link fromString} instead.
   */
  public static parse(iban: string): LegacyIban {
    return new LegacyIban(Fn.pipe(iban, fromString, electronicFormat));
  }

  /**
   * The IBAN as a string
   */
  public get value(): string {
    return this._value;
  }
  private get _asUnvalidated(): UnvalidatedIban {
    return UnvalidatedIban({
      countryCode: this.countryCode,
      checkDigits: this.checkDigits,
      bban: this.bban,
    });
  }

  /**
   * @returns
   * The ISO 3166-1 alpha-2 country code
   */
  public get countryCode(): string {
    return this.value.substring(0, 2).toUpperCase();
  }

  /**
   * @returns
   * The check digits
   */
  public get checkDigits(): string {
    return this.value.substring(2, 4);
  }

  /**
   * @returns
   * The Basic Bank Account Number (BBAN)
   */
  public get bban(): string {
    return this.value.substring(4).toUpperCase();
  }

  /**
   * @param separator Separator between groups, defaults to ' '
   *
   * @returns
   * IBAN as string, grouped in groups of 4
   */
  public format(separator: string = ' '): string {
    return Fn.pipe(this._asUnvalidated, printFormat(separator));
  }

  /**
   * @returns
   * `true`, if the check digits are valid
   */
  public checkDigitsValid(): boolean {
    return Fn.pipe(this._asUnvalidated, validate, Either.isRight);
  }
}
